'use client';

import { useState } from 'react';

import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';

interface ToolbarZoomControlProps {
  zoomPercent: number;
  canZoomOut: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
  disabled?: boolean;
}

export function ToolbarZoomControl({
  zoomPercent,
  canZoomOut,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  disabled = false,
}: ToolbarZoomControlProps) {
  const t = useTranslations();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-label={t('get-feedback-zoom-label')}
        aria-expanded={open}
        className={`flex items-center gap-1 rounded-full border bg-background px-3 py-2 text-xs font-medium text-blue-950 shadow-lg transition-all hover:bg-blue-50 hover:text-blue-950 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400 ${open ? 'border-[#172554]' : 'border-slate-200'}`}
      >
        {zoomPercent}%
        <ChevronDown
          className={`h-3 w-3 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="absolute left-0 bottom-full z-50 mb-1.5 min-w-[120px] overflow-hidden rounded-xl border border-border bg-background shadow-md">
            {(
              [
                {
                  label: t('get-feedback-zoom-zoomIn'),
                  action: onZoomIn,
                  disabled: false,
                },
                {
                  label: t('get-feedback-zoom-zoomOut'),
                  action: onZoomOut,
                  disabled: !canZoomOut,
                },
                {
                  label: t('get-feedback-zoom-reset'),
                  action: onResetZoom,
                  disabled: false,
                },
              ] as const
            ).map(({ label, action, disabled }) => (
              <button
                key={label}
                type="button"
                onClick={action}
                disabled={disabled}
                className="flex w-full items-center px-4 py-2 text-xs text-black hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
              >
                {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
